import { Injectable, inject, signal, type Signal, type WritableSignal } from "@angular/core";
import { HttpClient, httpResource, type HttpResourceRef } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

import { environment } from "../../../environments/environment";
import { ActingOrgService } from "./acting-org.service";

import type { IAuthEvent } from "../models/i-auth-event";
import type { IPortalActivityEvent } from "../models/i-portal-activity-event";
import type { IPortalDocument } from "../models/i-portal-document";
import type { IPortalInsight } from "../models/i-portal-insight";
import type { IPortalKpi } from "../models/i-portal-kpi";
import type { IPortalReport } from "../models/i-portal-report";
import type { IReportView } from "../models/i-report-view";
import type { IPortalRollout, RolloutRange } from "../models/i-portal-rollout";
import type { IPortalSite } from "../models/i-portal-site";

const ACTIVITY_LIMIT = 25;

/** Feed rows the dashboard card shows. The Activity page asks for more. */
const FEED_LIMIT = 6;

/** The chart range on first paint. Matches the design's default selection. */
const DEFAULT_RANGE: RolloutRange = "30d";

/**
 * Empty rollout, used as the resource's default so the charts can render their
 * axes and empty states before the first response lands.
 */
const EMPTY_ROLLOUT: IPortalRollout = {
  range: DEFAULT_RANGE,
  totalVisits: 0,
  buckets: [],
  statusMix: [],
  installDensity: [],
};

/**
 * The customer-facing content, as signal resources.
 *
 * Every resource's URL is a function of `actingOrg.slug()`, which is what makes
 * "view as" work without a page reload: switching organization changes the URL
 * the resource depends on, so it refetches. The slug itself is NOT in the URL —
 * it travels as `X-Acting-Org` via the interceptor, and the API decides from
 * the verified token whether to honour it. The cache-buster is the only reason
 * the slug appears here at all.
 */
@Injectable({ providedIn: "root" })
export class PortalDataService {

  private readonly http = inject(HttpClient);

  private readonly actingOrg = inject(ActingOrgService);

  private url(path: string): string {
    const acting: string | null = this.actingOrg.slug();
    const scope: string = acting ?? "self";
    // `scope` is a cache key, not authorization. Without it the resource would
    // not re-request when the admin switches organization, and the previous
    // customer's rows would stay on screen under the new customer's branding.
    // Encoded even though a slug is a DNS label by construction: the value
    // reaches here from session storage, and one stray `&` would silently
    // rewrite the rest of the query string.
    return (
      `${environment.apiBaseUrl}${path}` +
      `${path.includes("?") ? "&" : "?"}scope=${encodeURIComponent(scope)}`
    );
  }

  public readonly kpis: HttpResourceRef<IPortalKpi[]> = httpResource<IPortalKpi[]>(
    () => this.url("/portal/kpis"),
    { defaultValue: [] }
  );

  /** Reports the customer built and kept, newest first. */
  public readonly reportViews: HttpResourceRef<IReportView[]> = httpResource<IReportView[]>(
    () => this.url("/portal/report-views"),
    { defaultValue: [] }
  );

  /**
   * Rename a saved report.
   *
   * The renamed row replaces the old one in the list already held, so the list
   * and an open report page both show the new name without a refetch. It keeps
   * its place in the list rather than jumping to the top, even though its
   * `updatedAt` moved — a row leaping away from the button just clicked is
   * disorienting. The next load puts it back in server order.
   */
  public async renameReportView(id: string, name: string): Promise<IReportView> {
    const renamed: IReportView = await firstValueFrom(
      this.http.patch<IReportView>(
        `${environment.apiBaseUrl}/portal/report-views/${encodeURIComponent(id)}`,
        { name }
      )
    );

    this.replaceReportView(renamed);
    return renamed;
  }

  /**
   * Swap a changed report into the list already held — after a rename or an
   * edit — so the list and an open report page show it without a refetch.
   */
  public replaceReportView(changed: IReportView): void {
    this.reportViews.update((views: IReportView[]): IReportView[] =>
      views.map((view: IReportView): IReportView => (view.id === changed.id ? changed : view))
    );
  }

  /**
   * Whether another saved report already has this name, ignoring case and
   * surrounding spaces. A hint for the name box, not a rule — the API allows
   * duplicates.
   */
  public isReportNameTaken(name: string, exceptId: string | null = null): boolean {
    const wanted: string = name.trim()
      .toLowerCase();
    return this.reportViews.value()
      .some(
        (view: IReportView): boolean =>
          view.id !== exceptId && view.name.trim()
            .toLowerCase() === wanted
      );
  }

  public readonly reports: HttpResourceRef<IPortalReport[]> = httpResource<IPortalReport[]>(
    () => this.url("/portal/reports"),
    { defaultValue: [] }
  );

  public readonly documents: HttpResourceRef<IPortalDocument[]> =
    httpResource<IPortalDocument[]>(() => this.url("/portal/documents"), {
      defaultValue: [],
    });

  public readonly authEvents: HttpResourceRef<IAuthEvent[]> = httpResource<IAuthEvent[]>(
    () => this.url(`/portal/auth-events?limit=${ACTIVITY_LIMIT}`),
    { defaultValue: [] }
  );

  public readonly sites: HttpResourceRef<IPortalSite[]> = httpResource<IPortalSite[]>(
    () => this.url("/portal/sites"),
    { defaultValue: [] }
  );

  public readonly insights: HttpResourceRef<IPortalInsight[]> = httpResource<IPortalInsight[]>(
    () => this.url("/portal/insights"),
    { defaultValue: [] }
  );

  public readonly activity: HttpResourceRef<IPortalActivityEvent[]> =
    httpResource<IPortalActivityEvent[]>(() => this.url(`/portal/activity?limit=${FEED_LIMIT}`), {
      defaultValue: [],
    });

  private readonly rangeSignal: WritableSignal<RolloutRange> =
    signal<RolloutRange>(DEFAULT_RANGE);

  /** The chart range currently selected. Read by the segmented control. */
  public readonly range: Signal<RolloutRange> = this.rangeSignal.asReadonly();

  /**
   * The charts, refetched when the range changes.
   *
   * The range is part of the URL, so switching it is a resource dependency
   * change rather than an imperative reload — which is also what keeps the
   * previous range's response from landing after the new one and winning.
   */
  public readonly rollout: HttpResourceRef<IPortalRollout> = httpResource<IPortalRollout>(
    () => this.url(`/portal/rollout?range=${this.rangeSignal()}`),
    { defaultValue: EMPTY_ROLLOUT }
  );

  public setRange(range: RolloutRange): void {
    this.rangeSignal.set(range);
  }

  private readonly exportingSignal: WritableSignal<boolean> = signal<boolean>(false);

  public readonly exporting: Signal<boolean> = this.exportingSignal.asReadonly();

  /**
   * Download the activity CSV.
   *
   * Fetched through HttpClient rather than pointed at with a link, because the
   * bearer token and `X-Acting-Org` are attached by the interceptor — a plain
   * anchor would send neither and the download would 401.
   */
  public async exportAuthEventsCsv(): Promise<void> {
    this.exportingSignal.set(true);
    try {
      const blob: Blob = await firstValueFrom(
        this.http.get(`${environment.apiBaseUrl}/portal/auth-events/export`, {
          responseType: "blob",
        })
      );

      const href: string = URL.createObjectURL(blob);
      const anchor: HTMLAnchorElement = document.createElement("a");
      anchor.href = href;
      anchor.download = `sign-in-activity-${this.actingOrg.slug() ?? "portal"}.csv`;
      anchor.click();
      // Revoking immediately would race the browser's own read of the blob in
      // some engines; one tick is enough and costs nothing.
      setTimeout((): void => URL.revokeObjectURL(href), 0);
    } finally {
      this.exportingSignal.set(false);
    }
  }

  /** Re-fetch everything — used after switching the acting organization. */
  public reloadAll(): void {
    this.kpis.reload();
    this.reports.reload();
    this.documents.reload();
    this.authEvents.reload();
    this.sites.reload();
    this.insights.reload();
    this.activity.reload();
    this.rollout.reload();
  }
}
